import React, { useEffect, useState } from 'react';
import { merchantApi } from '../../api/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faWallet, faCoins, faCalendarDay, faCalendar,
  faClipboardList, faClock, faUserShield, faArrowTrendUp,
  faMoneyBillTransfer, faSnowflake, faArrowDown, faArrowUp, faChartPie, faGift
} from '@fortawesome/free-solid-svg-icons';
import { UiPageHeader, UiStatsGrid, UiButton } from '../../components/ui';
import type { StatCardDef } from '../../components/ui';

interface DashboardData {
  total_merchants: number;
  active_merchants: number;
  wallet_balance: number;
  total_commission: number;
  total_withdrawn: number;      // v2: ถอนจริงเท่านั้น (ไม่รวม freeze)
  withdrawn_count: number;
  total_frozen: number;         // v2: ยอดอายัดคงเหลือ (freeze - unfreeze_return)
  frozen_count: number;
  today_commission: number;
  today_payin: number;
  today_payout: number;
  month_commission: number;
  month_payin: number;
  month_payout: number;
  total_payin_commission: number;
  total_payout_commission: number;
  total_orders_paid: number;
  total_revenue: number;
  pending_requests: number;
  generated_at?: string | null;
}

// หัวข้อหมวดการ์ด — ให้แต่ละกลุ่มอ่านแยกกันชัด ๆ
const SectionHeader: React.FC<{ icon: any; title: string; tint?: string; tintStyle?: React.CSSProperties }> = ({ icon, title, tint = '', tintStyle }) => (
  <div className="flex items-center gap-2.5 mb-3">
    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${tint}`} style={tintStyle}>
      <FontAwesomeIcon icon={icon} className="text-xs" />
    </div>
    <h2 className="text-sm font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wide">{title}</h2>
    <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700" />
  </div>
);

interface SubSpread {
  today: number;       month: number;
  todayPayin: number;  todayPayout: number;
  monthPayin: number;  monthPayout: number;
}

const fmt = (n: number) => new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(n);

// การ์ด custom ทรงเดียวกับ house card (มี breakdown rows เกิน StatCardDef)
const CARD_CLASS = 'bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6';

const ResellerDashboardPage: React.FC = () => {
  const resellerUser = JSON.parse(localStorage.getItem('reseller_user') || '{}');
  const isSubReseller = !!resellerUser?.parent_reseller_id;

  const [data, setData] = useState<DashboardData | null>(null);
  const [sub, setSub] = useState<SubSpread>({ today: 0, month: 0, todayPayin: 0, todayPayout: 0, monthPayin: 0, monthPayout: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const bkk = new Date(Date.now() + 7 * 3600 * 1000);
      const today = bkk.toISOString().split('T')[0];
      const firstOfMonth = `${today.slice(0, 7)}-01`;

      const calls: Promise<any>[] = [merchantApi.get('/reseller/dashboard')];
      if (!isSubReseller) {
        calls.push(
          merchantApi.get(`/reseller/sub-commissions/summary?start_date=${today}&end_date=${today}`),
          merchantApi.get(`/reseller/sub-commissions/summary?start_date=${firstOfMonth}&end_date=${today}`)
        );
      }
      const [dashRes, todaySubRes, monthSubRes] = await Promise.all(calls);
      setData(dashRes.data.data);
      if (!isSubReseller) {
        const t = todaySubRes?.data?.data?.total || {};
        const m = monthSubRes?.data?.data?.total || {};
        setSub({
          today:        t.total_amount  || 0,
          todayPayin:   t.payin_amount  || 0,
          todayPayout:  t.payout_amount || 0,
          month:        m.total_amount  || 0,
          monthPayin:   m.payin_amount  || 0,
          monthPayout:  m.payout_amount || 0,
        });
      }
    } catch (err: any) {
      console.error('Error loading dashboard:', err);
      setData(null);
      setSub({ today: 0, month: 0, todayPayin: 0, todayPayout: 0, monthPayin: 0, monthPayout: 0 });
      setLoadError(err?.response?.data?.message || err?.message || 'โหลด Dashboard ไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2"
          style={{ borderColor: 'var(--theme-primary)' }}
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-2xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-900/20 px-5 py-8 text-center">
        <FontAwesomeIcon icon={faWallet} className="text-3xl text-red-300 dark:text-red-500/60 mb-3" />
        <p className="font-semibold text-red-700 dark:text-red-400">โหลด Dashboard ไม่สำเร็จ</p>
        <p className="text-sm text-red-600/80 dark:text-red-400/80 mt-1">{loadError || 'กรุณารีเฟรชหน้าอีกครั้ง'}</p>
        <UiButton
          variant="danger"
          className="mt-4"
          onClick={() => { setLoading(true); loadAll(); }}
        >
          ลองใหม่
        </UiButton>
      </div>
    );
  }

  const d = data;
  const hasSubSpread = !isSubReseller && (sub.today > 0 || sub.month > 0);

  // total_commission จาก RPC มาจาก total_credit ซึ่งรวม parent spread ไว้แล้ว
  const totalCommission = d.total_commission || 0;
  const todayMerchant = d.today_commission || 0;
  const monthMerchant = d.month_commission || 0;

  const overviewCards: StatCardDef[] = [
    {
      icon: faUsers,
      iconClassName: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400',
      label: 'Merchants',
      value: (
        <span className="text-gray-800 dark:text-slate-100">
          {d.active_merchants || 0}
          <span className="text-sm font-normal text-gray-400 dark:text-slate-500"> / {d.total_merchants || 0}</span>
        </span>
      ),
      subValue: <span className="text-gray-400 dark:text-slate-500">Active / Total</span>,
    },
    {
      icon: faClipboardList,
      iconClassName: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
      label: 'Total Paid Orders',
      value: <span className="text-gray-800 dark:text-slate-100 tabular-nums">{(d.total_orders_paid || 0).toLocaleString()}</span>,
    },
    {
      icon: faCoins,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'Total Revenue',
      value: <span className="tabular-nums" style={{ color: 'var(--theme-primary)' }}>฿{fmt(d.total_revenue || 0)}</span>,
    },
    {
      icon: faClock,
      iconClassName: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'Pending Requests',
      value: <span className="text-gray-800 dark:text-slate-100 tabular-nums">{d.pending_requests || 0}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">รอ Admin อนุมัติ</span>,
    },
  ];

  return (
    <div>
      <UiPageHeader
        icon={faChartPie}
        title="Dashboard"
        subtitle="Overview of your reseller performance"
        below={
          d.generated_at ? (
            <p className="text-xs text-white/60 flex items-center gap-1.5 mt-1.5">
              <FontAwesomeIcon icon={faClock} className="text-[10px]" />
              ข้อมูล ณ {new Date(d.generated_at).toLocaleString('th-TH', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </p>
          ) : undefined
        }
      />

      {/* ══ หมวด 1: กระเป๋าเงิน — คงเหลือ / คอมสะสม / ถอนจริง / อายัด (แยกชัด ไม่ปนกัน) ══ */}
      <SectionHeader
        icon={faWallet}
        title="กระเป๋าเงิน"
        tintStyle={{ background: 'var(--theme-primary-light)', color: 'var(--theme-primary)' }}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">

        {/* Wallet Balance */}
        <div
          className="rounded-2xl p-6 text-white"
          style={{ background: 'var(--theme-primary-gradient)', boxShadow: 'var(--theme-shadow-primary)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
              <FontAwesomeIcon icon={faWallet} className="text-lg" />
            </div>
            <FontAwesomeIcon icon={faArrowTrendUp} className="text-white/40 text-xl" />
          </div>
          <p className="text-white/70 text-xs uppercase font-medium tracking-wide mb-1">Balance (ถอนได้)</p>
          <p className="text-2xl font-bold tabular-nums">฿{fmt(d.wallet_balance || 0)}</p>
          <p className="text-white/50 text-xs mt-2">ยอดคงเหลือที่ถอนได้จริง</p>
        </div>

        {/* Commission สะสมทั้งหมด */}
        <div className={CARD_CLASS}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
              <FontAwesomeIcon icon={faCoins} className="text-lg" />
            </div>
          </div>
          <p className="text-gray-400 dark:text-slate-500 text-xs uppercase font-medium tracking-wide mb-1">Commission สะสม</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-slate-100 tabular-nums">฿{fmt(totalCommission)}</p>
          <div className="mt-2 space-y-1 pt-2 border-t border-gray-100 dark:border-slate-700">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                <FontAwesomeIcon icon={faArrowDown} className="text-[9px]" /> ค่าคอมฝาก
              </span>
              <span className="font-semibold text-green-600 dark:text-green-400 tabular-nums">฿{fmt(d.total_payin_commission || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                <FontAwesomeIcon icon={faArrowUp} className="text-[9px]" /> ค่าคอมถอน
              </span>
              <span className="font-semibold tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{fmt(d.total_payout_commission || 0)}</span>
            </div>
          </div>
        </div>

        {/* ถอนแล้วจริง — ไม่รวม freeze */}
        <div className={`${CARD_CLASS} border-l-4 border-l-red-400`}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400">
              <FontAwesomeIcon icon={faMoneyBillTransfer} className="text-lg" />
            </div>
          </div>
          <p className="text-gray-400 dark:text-slate-500 text-xs uppercase font-medium tracking-wide mb-1">ถอนแล้ว (Withdrawn)</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-slate-100 tabular-nums">฿{fmt(d.total_withdrawn || 0)}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500 mt-2">เงินออกจริง {d.withdrawn_count || 0} ครั้ง · ไม่รวมอายัด</p>
        </div>

        {/* อายัดอยู่ — แยกออกจากยอดถอน */}
        <div className={`${CARD_CLASS} border-l-4 border-l-purple-400`}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
              <FontAwesomeIcon icon={faSnowflake} className="text-lg" />
            </div>
          </div>
          <p className="text-gray-400 dark:text-slate-500 text-xs uppercase font-medium tracking-wide mb-1">อายัดอยู่ (Frozen)</p>
          <p className="text-2xl font-bold text-purple-700 dark:text-purple-400 tabular-nums">฿{fmt(d.total_frozen || 0)}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500 mt-2">
            {(d.total_frozen || 0) > 0 ? `ถูกอายัด ${d.frozen_count || 0} ครั้ง · ยังไม่คืน/ถอน` : 'ไม่มียอดถูกอายัด'}
          </p>
        </div>
      </div>

      {/* ══ หมวด 2: Commission วันนี้ / เดือนนี้ (แยกฝาก-ถอน + Sub) ══ */}
      <SectionHeader icon={faGift} title="Commission" tint="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">

        {/* Commission Today */}
        <div className={CARD_CLASS}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <FontAwesomeIcon icon={faCalendarDay} className="text-lg" />
            </div>
            {hasSubSpread && sub.today > 0 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400">+Sub</span>
            )}
          </div>
          <p className="text-gray-400 dark:text-slate-500 text-xs uppercase font-medium tracking-wide mb-1">Commission Today</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-slate-100 tabular-nums">
            ฿{fmt(todayMerchant + sub.today)}
          </p>
          <div className="mt-2 space-y-1 pt-2 border-t border-gray-100 dark:border-slate-700">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                <FontAwesomeIcon icon={faArrowDown} className="text-[9px]" /> ค่าคอมฝาก
              </span>
              <span className="font-semibold text-green-600 dark:text-green-400 tabular-nums">฿{fmt(d.today_payin || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                <FontAwesomeIcon icon={faArrowUp} className="text-[9px]" /> ค่าคอมถอน
              </span>
              <span className="font-semibold tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{fmt(d.today_payout || 0)}</span>
            </div>
            {hasSubSpread && sub.today > 0 && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                  <FontAwesomeIcon icon={faUserShield} className="text-[9px]" /> Sub Spread (In {fmt(sub.todayPayin)} / Out {fmt(sub.todayPayout)})
                </span>
                <span className="font-semibold text-amber-500 dark:text-amber-400 tabular-nums">฿{fmt(sub.today)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Commission This Month */}
        <div className={CARD_CLASS}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 dark:text-violet-400">
              <FontAwesomeIcon icon={faCalendar} className="text-lg" />
            </div>
            {hasSubSpread && sub.month > 0 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400">+Sub</span>
            )}
          </div>
          <p className="text-gray-400 dark:text-slate-500 text-xs uppercase font-medium tracking-wide mb-1">Commission This Month</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-slate-100 tabular-nums">
            ฿{fmt(monthMerchant + sub.month)}
          </p>
          <div className="mt-2 space-y-1 pt-2 border-t border-gray-100 dark:border-slate-700">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                <FontAwesomeIcon icon={faArrowDown} className="text-[9px]" /> ค่าคอมฝาก
              </span>
              <span className="font-semibold text-green-600 dark:text-green-400 tabular-nums">฿{fmt(d.month_payin || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                <FontAwesomeIcon icon={faArrowUp} className="text-[9px]" /> ค่าคอมถอน
              </span>
              <span className="font-semibold tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{fmt(d.month_payout || 0)}</span>
            </div>
            {hasSubSpread && sub.month > 0 && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                  <FontAwesomeIcon icon={faUserShield} className="text-[9px]" /> Sub Spread (In {fmt(sub.monthPayin)} / Out {fmt(sub.monthPayout)})
                </span>
                <span className="font-semibold text-amber-500 dark:text-amber-400 tabular-nums">฿{fmt(sub.month)}</span>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ══ หมวด 3: ภาพรวมธุรกิจ ══ */}
      <SectionHeader icon={faChartPie} title="ภาพรวม" tint="bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400" />
      <UiStatsGrid cols={4} cards={overviewCards} />
    </div>
  );
};

export default ResellerDashboardPage;
